/**
 * Tela da frota: veículos, manutenções e oficinas.
 *
 * Papel: junta as três seções do módulo, cada uma com a sua tabela, o seu
 * formulário em caixa de diálogo e a sua confirmação de exclusão.
 *
 * Como usar: as três ficam na mesma tela porque é assim que se leem — o carro,
 * o que foi feito nele e quem fez. Quem tem nível `ver` não recebe `onNovo`,
 * `onEditar` nem `onRemover`, e por isso não vê botão de ação nenhum.
 *
 * Excluir um veículo que ainda tem manutenção responde 409, e a mensagem
 * aparece dentro da própria caixa de confirmação: o histórico de gastos é o que
 * se quer preservar quando o carro sai da frota, e fechar a caixa antes da
 * resposta esconderia a explicação.
 */
import {
  type ManutencaoVeiculo,
  type Oficina,
  type Veiculo,
  acessoAlcanca,
} from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import { formatarPlaca } from '@/shared/lib/formato';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { SecaoCadastro } from '@/shared/ui/secao-cadastro';
import { FormularioManutencao } from '../components/formulario-manutencao';
import { FormularioOficina } from '../components/formulario-oficina';
import { FormularioVeiculo } from '../components/formulario-veiculo';
import { TabelaManutencoes } from '../components/tabela-manutencoes';
import { TabelaOficinas } from '../components/tabela-oficinas';
import { TabelaVeiculos } from '../components/tabela-veiculos';
import {
  useExcluirManutencao,
  useExcluirOficina,
  useExcluirVeiculo,
  useManutencoes,
  useOficinas,
  useVeiculos,
} from '../hooks/usar-veiculos';

/** Em que estado a caixa do formulário está: fechada, criando ou editando algo. */
type PainelAberto =
  | { modo: 'fechado' }
  | { modo: 'novo-veiculo' }
  | { modo: 'edicao-veiculo'; veiculo: Veiculo }
  | { modo: 'nova-manutencao' }
  | { modo: 'edicao-manutencao'; manutencao: ManutencaoVeiculo }
  | { modo: 'nova-oficina' }
  | { modo: 'edicao-oficina'; oficina: Oficina };

/** O que está esperando confirmação de exclusão. */
type AguardandoRemocao =
  | { tipo: 'veiculo'; veiculo: Veiculo }
  | { tipo: 'manutencao'; manutencao: ManutencaoVeiculo }
  | { tipo: 'oficina'; oficina: Oficina };

/**
 * Escreve o que a caixa de confirmação pergunta.
 *
 * @param alvo - O que está esperando confirmação.
 * @returns O nome do registro, para o título da caixa.
 */
function nomeDoAlvo(alvo: AguardandoRemocao): string {
  if (alvo.tipo === 'veiculo') {
    return `${alvo.veiculo.codigo} — ${formatarPlaca(alvo.veiculo.placa)}`;
  }
  if (alvo.tipo === 'oficina') return alvo.oficina.nome;
  return `${alvo.manutencao.tipo} de ${alvo.manutencao.veiculo.codigo}`;
}

/**
 * Página de Veículos.
 *
 * @returns A tela com as três seções, a caixa do formulário e a confirmação.
 */
export function PaginaVeiculos() {
  const [painel, setPainel] = useState<PainelAberto>({ modo: 'fechado' });
  const [aRemover, setARemover] = useState<AguardandoRemocao | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'veiculos', 'editar');

  const consultaFrota = useVeiculos();
  const consultaManutencoes = useManutencoes();
  const consultaOficinas = useOficinas();
  const excluirVeiculo = useExcluirVeiculo();
  const excluirManutencao = useExcluirManutencao();
  const excluirOficina = useExcluirOficina();

  const removendo =
    excluirVeiculo.isPending || excluirManutencao.isPending || excluirOficina.isPending;

  /** Fecha a caixa, seja pelo botão, pelo `Esc` ou por clique no véu em volta dela. */
  function handlePainelOpenChange(aberto: boolean) {
    if (!aberto) setPainel({ modo: 'fechado' });
  }

  /** Abre a confirmação de exclusão, limpando a falha da tentativa anterior. */
  function handlePedirRemocao(alvo: AguardandoRemocao) {
    setErroDaRemocao(null);
    setARemover(alvo);
  }

  /** Fecha a confirmação de exclusão, com a falha anterior junto. */
  function handleRemocaoOpenChange(aberto: boolean) {
    if (aberto) return;
    setARemover(null);
    setErroDaRemocao(null);
  }

  /** Apaga o registro confirmado e fecha a caixa, se a API aceitar. */
  async function handleConfirmarRemocao() {
    if (aRemover === null) return;
    setErroDaRemocao(null);

    try {
      if (aRemover.tipo === 'veiculo') await excluirVeiculo.mutateAsync(aRemover.veiculo.id);
      else if (aRemover.tipo === 'manutencao') {
        await excluirManutencao.mutateAsync(aRemover.manutencao.id);
      } else await excluirOficina.mutateAsync(aRemover.oficina.id);
      setARemover(null);
    } catch (erro) {
      setErroDaRemocao(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A exclusão falhou antes de chegar ao servidor.',
      );
    }
  }

  return (
    <div>
      <CabecalhoDoModulo
        titulo="Veículos"
        subtitulo="CADASTRO E MANUTENÇÕES DA FROTA DA FAMÍLIA"
      />

      <SecaoCadastro
        titulo="Veículos da família"
        rotuloNovo="+ Novo veículo"
        onNovo={podeEditar ? () => setPainel({ modo: 'novo-veiculo' }) : undefined}
        carregando={consultaFrota.isPending}
        erro={consultaFrota.error}
        onTentarDeNovo={() => void consultaFrota.refetch()}
        vazia={(consultaFrota.data ?? []).length === 0}
        mensagemVazia="Nenhum veículo cadastrado ainda."
      >
        <TabelaVeiculos
          veiculos={consultaFrota.data ?? []}
          onEditar={
            podeEditar
              ? (veiculo) => setPainel({ modo: 'edicao-veiculo', veiculo })
              : undefined
          }
          onRemover={
            podeEditar
              ? (veiculo) => handlePedirRemocao({ tipo: 'veiculo', veiculo })
              : undefined
          }
        />
      </SecaoCadastro>

      <SecaoCadastro
        titulo="Histórico de manutenções"
        rotuloNovo="+ Nova manutenção"
        onNovo={podeEditar ? () => setPainel({ modo: 'nova-manutencao' }) : undefined}
        carregando={consultaManutencoes.isPending}
        erro={consultaManutencoes.error}
        onTentarDeNovo={() => void consultaManutencoes.refetch()}
        vazia={(consultaManutencoes.data ?? []).length === 0}
        mensagemVazia="Nenhuma manutenção registrada."
      >
        <TabelaManutencoes
          manutencoes={consultaManutencoes.data ?? []}
          onEditar={
            podeEditar
              ? (manutencao) => setPainel({ modo: 'edicao-manutencao', manutencao })
              : undefined
          }
          onRemover={
            podeEditar
              ? (manutencao) => handlePedirRemocao({ tipo: 'manutencao', manutencao })
              : undefined
          }
        />
      </SecaoCadastro>

      <SecaoCadastro
        titulo="Contatos de manutenção, blindagem e serviços"
        rotuloNovo="+ Novo contato"
        onNovo={podeEditar ? () => setPainel({ modo: 'nova-oficina' }) : undefined}
        carregando={consultaOficinas.isPending}
        erro={consultaOficinas.error}
        onTentarDeNovo={() => void consultaOficinas.refetch()}
        vazia={(consultaOficinas.data ?? []).length === 0}
        mensagemVazia="Nenhum contato cadastrado ainda."
      >
        <TabelaOficinas
          oficinas={consultaOficinas.data ?? []}
          onEditar={
            podeEditar
              ? (oficina) => setPainel({ modo: 'edicao-oficina', oficina })
              : undefined
          }
          onRemover={
            podeEditar
              ? (oficina) => handlePedirRemocao({ tipo: 'oficina', oficina })
              : undefined
          }
        />
      </SecaoCadastro>

      <Dialog open={painel.modo !== 'fechado'} onOpenChange={handlePainelOpenChange}>
        <DialogContent>
          {painel.modo !== 'fechado' && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {painel.modo === 'novo-veiculo' && 'Novo veículo'}
                  {painel.modo === 'edicao-veiculo' &&
                    `${painel.veiculo.codigo} — ${painel.veiculo.marca}`}
                  {painel.modo === 'nova-manutencao' && 'Nova manutenção'}
                  {painel.modo === 'edicao-manutencao' &&
                    `${painel.manutencao.tipo} — ${painel.manutencao.veiculo.codigo}`}
                  {painel.modo === 'nova-oficina' && 'Novo contato'}
                  {painel.modo === 'edicao-oficina' && painel.oficina.nome}
                </DialogTitle>
              </DialogHeader>

              {(painel.modo === 'novo-veiculo' || painel.modo === 'edicao-veiculo') && (
                <FormularioVeiculo
                  key={painel.modo === 'novo-veiculo' ? 'novo' : painel.veiculo.id}
                  veiculo={painel.modo === 'novo-veiculo' ? null : painel.veiculo}
                  onSalvo={() => setPainel({ modo: 'fechado' })}
                  onCancelar={() => setPainel({ modo: 'fechado' })}
                />
              )}

              {(painel.modo === 'nova-manutencao' || painel.modo === 'edicao-manutencao') && (
                <FormularioManutencao
                  key={painel.modo === 'nova-manutencao' ? 'nova' : painel.manutencao.id}
                  manutencao={painel.modo === 'nova-manutencao' ? null : painel.manutencao}
                  onSalvo={() => setPainel({ modo: 'fechado' })}
                  onCancelar={() => setPainel({ modo: 'fechado' })}
                />
              )}

              {(painel.modo === 'nova-oficina' || painel.modo === 'edicao-oficina') && (
                <FormularioOficina
                  key={painel.modo === 'nova-oficina' ? 'nova' : painel.oficina.id}
                  oficina={painel.modo === 'nova-oficina' ? null : painel.oficina}
                  onSalvo={() => setPainel({ modo: 'fechado' })}
                  onCancelar={() => setPainel({ modo: 'fechado' })}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={aRemover !== null} onOpenChange={handleRemocaoOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remover {aRemover === null ? '' : nomeDoAlvo(aRemover)}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {aRemover?.tipo === 'veiculo'
                ? 'O veículo sai da frota. O histórico de manutenção dele precisa sair antes.'
                : 'A linha sai do cadastro. Não há como desfazer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {erroDaRemocao !== null && (
            <p className="text-sm text-destructive" role="alert">
              {erroDaRemocao}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={removendo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={removendo}
              onClick={(evento) => {
                // O `AlertDialogAction` fecha a caixa ao ser clicado, e fechá-la
                // antes da resposta da API desmontaria o estado de envio e
                // esconderia a recusa por histórico em uso.
                evento.preventDefault();
                void handleConfirmarRemocao();
              }}
            >
              {removendo ? 'Removendo…' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
